/**
 * TryPage - Public Demo Experience
 *
 * A public, no-login page where new users can try a sample practice session.
 * This is a teaser experience to encourage sign-ups. It is text only.
 *
 * DEMO VOICE: RETIRED. The disabled "Try with Voice — Coming soon" option had
 * no handler and no server support (POST /demo/practice accepts text only); a
 * real voice demo would need a paid provider call for an anonymous visitor
 * with no Central action to meter it. Voice is the canonical Live
 * Conversation after sign-in, which checks the microphone and reports
 * readiness truthfully.
 *
 * // TODO: enforce a per-session limit on how many times the demo can be run
 * // before nudging to sign up.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  useGetDemoScenariosQuery,
  useRunDemoPracticeMutation,
  DemoScenarioDto,
  DemoPracticeResponse,
} from '../store/api/demoApi';
import { useAnalytics } from '../hooks/useAnalytics';

type DemoStep = 'intro' | 'scenario' | 'input' | 'result';

export function TryPage() {
  const [step, setStep] = useState<DemoStep>('intro');
  const [selectedScenario, setSelectedScenario] = useState<DemoScenarioDto | null>(null);
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState<DemoPracticeResponse | null>(null);

  // Fetch demo scenarios
  const { data: scenarios = [], isLoading: scenariosLoading } = useGetDemoScenariosQuery();
  const [runDemoPractice, { isLoading: isAnalyzing, error: analyzeError }] = useRunDemoPracticeMutation();
  const { track } = useAnalytics();

  // Handle scenario selection
  const handleSelectScenario = (scenario: DemoScenarioDto) => {
    setSelectedScenario(scenario);
    setStep('input');
  };

  // Handle text submission
  const handleSubmitText = async () => {
    if (!answer.trim() || !selectedScenario) return;

    try {
      const response = await runDemoPractice({
        mode: 'text',
        answer: answer.trim(),
        scenarioId: selectedScenario.id,
      }).unwrap();
      setResult(response);
      setStep('result');
    } catch (err) {
      console.error('Demo practice failed:', err);
    }
  };

  // Start over
  const handleStartOver = () => {
    setStep('intro');
    setSelectedScenario(null);
    setAnswer('');
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-purple-50">
      {/* Header */}
      <header className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-primary-600">
            BeHeard
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Log in
            </Link>
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Sign up free
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Step: Intro */}
        {step === 'intro' && (
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                Try a 60-Second Practice Run
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                See how BeHeard helps you master your pitch, handle objections, and communicate with confidence.
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <button
                onClick={() => setStep('scenario')}
                data-testid="try-start-text"
                className="w-full p-6 rounded-xl border-2 border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all text-center"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TextIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div className="font-semibold text-gray-900">Try with Text</div>
                <div className="text-sm text-gray-500 mt-1">Type your response</div>
              </button>
            </div>

            {/* Features preview */}
            <div className="pt-8 border-t border-gray-100 mt-12">
              <p className="text-sm text-gray-500 mb-6">What you'll get in the full experience:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                <FeatureCard
                  icon={<SparklesIcon className="w-5 h-5" />}
                  title="AI Coaching"
                  description="Coaching conversations with practice personas, with AI feedback"
                />
                <FeatureCard
                  icon={<ChartIcon className="w-5 h-5" />}
                  title="Track Progress"
                  description="See your improvement over time with detailed analytics"
                />
                <FeatureCard
                  icon={<TrophyIcon className="w-5 h-5" />}
                  title="Leaderboards"
                  description="Compete with others and climb the ranks"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step: Scenario Selection */}
        {step === 'scenario' && (
          <div className="space-y-8">
            <div className="text-center">
              <button
                onClick={() => setStep('intro')}
                className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1"
              >
                <ArrowLeftIcon className="w-4 h-4" /> Back
              </button>
              <h2 className="text-2xl font-bold text-gray-900">Choose a Scenario</h2>
              <p className="text-gray-600 mt-2">Pick a situation to practice</p>
            </div>

            {scenariosLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="grid gap-4">
                {scenarios.map((scenario) => (
                  <button
                    key={scenario.id}
                    onClick={() => handleSelectScenario(scenario)}
                    className="p-6 rounded-xl border-2 border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all text-left"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{scenario.name}</h3>
                          <span
                            className={clsx(
                              'text-xs px-2 py-0.5 rounded-full',
                              scenario.difficulty === 'beginner' && 'bg-green-100 text-green-700',
                              scenario.difficulty === 'intermediate' && 'bg-yellow-100 text-yellow-700',
                              scenario.difficulty === 'advanced' && 'bg-red-100 text-red-700'
                            )}
                          >
                            {scenario.difficulty}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm">{scenario.description}</p>
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step: Input */}
        {step === 'input' && selectedScenario && (
          <div className="space-y-8">
            <div className="text-center">
              <button
                onClick={() => setStep('scenario')}
                className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1"
              >
                <ArrowLeftIcon className="w-4 h-4" /> Change scenario
              </button>
              <h2 className="text-2xl font-bold text-gray-900">{selectedScenario.name}</h2>
            </div>

            {/* Scenario prompt */}
            <div className="bg-gradient-to-r from-primary-100 to-purple-100 rounded-xl p-6 border border-primary-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <QuoteIcon className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-primary-800 mb-1">The Scenario</p>
                  <p className="text-gray-800">{selectedScenario.prompt}</p>
                </div>
              </div>
            </div>

            {/* Text input */}
            <div className="space-y-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Your Response</span>
                  <textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Type how you would respond..."
                    rows={6}
                    className="mt-2 w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                    maxLength={5000}
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-400">
                      {answer.length > 0 ? `${answer.split(/\s+/).filter(Boolean).length} words` : 'Minimum 10 characters'}
                    </span>
                    <span className="text-xs text-gray-400">{answer.length}/5000</span>
                  </div>
                </label>

                {analyzeError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  onClick={handleSubmitText}
                  disabled={answer.trim().length < 10 || isAnalyzing}
                  className={clsx(
                    'w-full py-3 px-6 rounded-xl font-semibold transition-all',
                    answer.trim().length >= 10 && !isAnalyzing
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  )}
                >
                  {isAnalyzing ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    'Submit Answer'
                  )}
                </button>
            </div>
          </div>
        )}

        {/* Step: Result */}
        {step === 'result' && result && (
          <div className="space-y-8">
            <DemoResult result={result} fallbackScenarioName={selectedScenario?.name} />

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-2">Ready to Get Serious?</h3>
              <p className="text-primary-100 mb-6">
                Create a free account to unlock the full BeHeard experience.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <Link
                  to="/login"
                  onClick={() => track('upgrade_cta_clicked', { source: 'try_page_signup' })}
                  className="px-8 py-3 bg-white text-primary-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
                >
                  Create Free Account
                </Link>
                <Link
                  to="/login"
                  onClick={() => track('upgrade_cta_clicked', { source: 'try_page_login' })}
                  className="px-8 py-3 border-2 border-white text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
                >
                  Log In
                </Link>
              </div>

              <ul className="text-sm text-primary-100 space-y-2">
                <li className="flex items-center justify-center gap-2">
                  <CheckIcon className="w-4 h-4" /> Save your sessions and track progress
                </li>
                <li className="flex items-center justify-center gap-2">
                  <CheckIcon className="w-4 h-4" /> Record &amp; review voice practice
                </li>
                <li className="flex items-center justify-center gap-2">
                  <CheckIcon className="w-4 h-4" /> Join the leaderboard & marketplace
                </li>
              </ul>
            </div>

            {/* Try again */}
            <div className="text-center">
              <button
                onClick={handleStartOver}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Try another scenario
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} BeHeard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// ============================================
// Helper Components
// ============================================

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-3">
        {icon}
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}

/**
 * The demo's result: the answer as given, and an explicit "not assessed"
 * state (UNAVAILABLE, 'demo_not_assessed'). No score, rating or count is
 * shown — the demo measures nothing, and a number here would be invented.
 */
export function DemoResult({ result, fallbackScenarioName }: { result: DemoPracticeResponse; fallbackScenarioName?: string }) {
  const scenarioName = result.demoScenario?.name ?? fallbackScenarioName;
  return (
    <>
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <CheckIcon className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Your Answer</h2>
        {scenarioName && <p className="text-gray-600 mt-1">{scenarioName}</p>}
      </div>

      {/* Not assessed: the demo has no measurement to report */}
      <div
        className="bg-white rounded-2xl shadow p-6"
        role="status"
        data-testid="demo-not-assessed"
        data-evidence-class={result.assessment.evidenceClass}
        data-reason={result.assessment.reason}
      >
        <h3 className="font-semibold text-gray-900 mb-2">Not assessed in the demo</h3>
        <p className="text-gray-700">{result.assessment.explanation}</p>
        <Link to="/login" className="mt-3 inline-block text-sm font-medium text-primary-600 hover:underline">
          Sign in to practise with real assessment
        </Link>
      </div>

      {/* Your answer */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-3">Your Response</h3>
        <p className="text-gray-700 whitespace-pre-wrap">{result.transcript}</p>
      </div>
    </>
  );
}

// ============================================
// Icons
// ============================================

function TextIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  );
}

function QuoteIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default TryPage;
